import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, MessageCircle, Sparkles } from 'lucide-react';
import { useAuth } from '../../src/contexts/AuthContext';
import { supabase } from '../../src/lib/supabase';
import {
    resumoEconomia, pontosDaRetencao, registrarPassoRetencao,
} from '../../src/lib/retencaoCancelamento';
import type { ResumoEconomia } from '../../src/lib/retencaoCancelamento';
import PortalConsumidorHeader from './PortalConsumidorHeader';
import { useMarcaPortal, useTituloPortal } from './marca/MarcaPortalContext';
import { linkWhatsapp } from './marca/marcaPortal';

/**
 * Tela de retenção (#5832): o que o cliente já ganhou, antes de abrir o pedido.
 *
 * Informa, não segura: "Quero seguir com o cancelamento" está na mesma tela e a
 * um clique. Cada passo (viu, seguiu, desistiu, falar com a Vigor) é registrado
 * para dar para responder se a retenção funciona — o registro nunca atrapalha o
 * fluxo de quem quer cancelar.
 *
 * O Portal não tem dark mode: só tema claro, como nas outras telas daqui.
 */
const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const PortalConsumidorRetencao: React.FC = () => {
    const { loading, signOut } = useAuth();
    const navigate = useNavigate();
    const [carregando, setCarregando] = useState(true);
    const [resumo, setResumo] = useState<ResumoEconomia | null>(null);
    const [ucId, setUcId] = useState<number | null>(null);
    // Quantas unidades o cliente tem: com mais de uma, o valor mostrado é a soma
    // de todas, e dizer isso evita a tela parecer inflada (o cliente ainda nem
    // escolheu qual unidade quer cancelar — isso é na tela seguinte).
    const [qtdUcs, setQtdUcs] = useState(0);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            // A RLS do Portal já limita faturas e UCs ao CPF logado.
            const [{ data: faturas }, { data: ucs }] = await Promise.all([
                // `excluido_em` é obrigatório aqui: a tela inicial do Portal filtra
                // igual, e sem isso a retenção somaria fatura excluída (reemissão,
                // duplicata corrigida) e mostraria economia MAIOR que a da home.
                supabase.from('faturas')
                    .select('economia, custo_s_vigor, valor_fatura_distribuidora, valor_boleto_vigor, mes_referencia')
                    .is('excluido_em', null),
                supabase.from('unidades_consumidoras').select('id').order('id'),
            ]);
            const r = resumoEconomia(faturas || []);
            setResumo(r);
            setQtdUcs((ucs || []).length);
            const primeiraUc = (ucs || [])[0]?.id ?? null;
            setUcId(primeiraUc);
            registrarPassoRetencao('viu', primeiraUc, r.acumulada || null);
        } catch (e) {
            console.error('Erro ao montar a tela de retenção', e);
            // Sem economia calculada a tela ainda abre, com a versão sem número.
            setResumo(resumoEconomia([]));
        } finally {
            setCarregando(false);
        }
    }, []);

    const { marca } = useMarcaPortal();

    useTituloPortal('Antes de cancelar');

    useEffect(() => {
        carregar();
    }, [carregar]);

    const seguir = () => {
        registrarPassoRetencao('seguiu', ucId, resumo?.acumulada ?? null);
        navigate('/portal-consumidor/cancelamento', { state: { veioDaRetencao: true } });
    };

    const desistir = () => {
        registrarPassoRetencao('desistiu', ucId, resumo?.acumulada ?? null);
        navigate('/portal-consumidor/inicio');
    };

    if (loading || carregando || !resumo) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    const pontos = pontosDaRetencao(resumo, brl, { comAvisoPrevio: marca.mostrarRegrasContratoVigor });

    return (
        <div className="min-h-screen bg-gray-50">
            <PortalConsumidorHeader />

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
                <div className="flex items-center gap-3">
                    <Link to="/portal-consumidor/ajuda" aria-label="Voltar para a Ajuda"
                        className="p-2 rounded-full text-gray-400 hover:text-primary hover:bg-gray-100 transition-colors">
                        <ArrowLeft size={20} />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Antes de cancelar</h1>
                        <p className="text-sm text-gray-500">Veja o que muda para você, e decida com calma.</p>
                    </div>
                </div>

                {resumo.temEconomia && (
                    <div className="bg-primary text-white rounded-2xl shadow-sm p-6 text-center">
                        <Sparkles size={24} className="mx-auto mb-2 opacity-90" aria-hidden="true" />
                        <p className="text-sm opacity-90">
                            {qtdUcs > 1 ? marca.economia.fraseVarias(qtdUcs) : marca.economia.frase}
                        </p>
                        <p className="text-3xl font-bold mt-1">{brl(resumo.acumulada)}</p>
                        {resumo.meses > 1 && (
                            <p className="text-xs opacity-90 mt-1">em {resumo.meses} faturas com desconto</p>
                        )}
                    </div>
                )}

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-100">
                    {pontos.map((p, i) => (
                        <div key={i} className="p-5">
                            <p className="text-sm font-bold text-gray-900">{p.titulo}</p>
                            <p className="text-sm text-gray-600 mt-1">{p.texto}</p>
                        </div>
                    ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                    <a href={linkWhatsapp(marca, 'Olá! Quero falar sobre minha assinatura antes de decidir o cancelamento.')}
                        target="_blank" rel="noopener noreferrer"
                        onClick={() => registrarPassoRetencao('falar_com_vigor', ucId, resumo.acumulada || null)}
                        className="inline-flex flex-1 items-center justify-center gap-2 py-3 px-5 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-colors">
                        <MessageCircle size={16} /> {marca.chave === 'vigor' ? 'Falar com a Vigor' : 'Falar com o atendimento'}
                    </a>
                    <button type="button" onClick={desistir}
                        className="inline-flex flex-1 items-center justify-center gap-2 py-3 px-5 rounded-xl text-sm font-bold text-primary bg-white border border-primary/20 hover:bg-primary/5 transition-colors">
                        Continuar como cliente
                    </button>
                </div>

                <div className="text-center">
                    <button type="button" onClick={seguir}
                        className="text-xs font-semibold text-gray-400 hover:text-gray-600 underline underline-offset-4 transition-colors">
                        Quero seguir com o cancelamento
                    </button>
                </div>
            </main>
        </div>
    );
};

export default PortalConsumidorRetencao;
